import { Button, ButtonRow, FormGroup, FormSection, TextArea } from '@ksyos/design-system';
import { useState } from 'react';
import { nonNullable } from '@common/utils/nonNullableTypeUtils';
import { createAddendum } from '@frontend/services/api/addendumApiService';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { ValidationErrors } from '../../../../../../utils/validation';
import type { IDiagnosticReportGetOne } from '../../../../../interfaces/diagnosticReportGetOne';
import type { IRequestGetOne } from '../../../../../interfaces/requestGetOne';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { addendumValidationSchema } from '../../../../../../../common/validation/addendum';
import { getSelectedLanguage } from '../../../../../../translate';
import { emptyValidationErrors, updateValidationErrorsForOne, validateOne } from '../../../../../../utils/validation';
import { ReloadEpisode } from '../../../../episodes/reloadEpisode';

interface IOsasAddendumAddProps {
    diagnosticReport?: IDiagnosticReportGetOne;
    processEntity: ProcessEntity;
    reloadProcess?: (reload?: ReloadEpisode) => Promise<void>;
    reloadRequest?: () => Promise<void>;
    request: IRequestGetOne;
}

export function OsasAddendumAdd(props: Readonly<IOsasAddendumAddProps>) {
    const abortSignal = useAbortSignal();
    const [message, setMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isEditing, setIsEditing] = useState<boolean>();

    const translate = getSelectedLanguage();

    if (props.processEntity === ProcessEntity.request) {
        if (!props.request.authorization?.canAddAddendum) {
            return null;
        }
    } else if (props.processEntity === ProcessEntity.diagnosticReport) {
        if (!props.diagnosticReport?.authorization?.canAddAddendum) {
            return null;
        }
    }

    if (isEditing) {
        return (
            <>
                <FormSection
                    isBorderless
                    title={translate.common.addendum}
                >
                    <FormGroup label={translate.common.addendum}>
                        <TextArea
                            className="automation-addendumValue"
                            error={getValidationErrorTranslation(errors.get('properties.message'))}
                            onBlur={() => validate()}
                            onChange={(e) => {
                                setMessage(e.target.value);
                                setErrors(emptyValidationErrors);
                            }}
                            placeholder={translate.addendum.placeholder}
                            rows={1}
                            value={message ?? ''}
                        />
                    </FormGroup>
                </FormSection>

                <ButtonRow
                    justify="right"
                    sx={{ marginTop: 8 }}
                >
                    <Button
                        onClick={() => {
                            setIsEditing(false);
                            setErrors(emptyValidationErrors);
                            setMessage(null);
                        }}
                        type="button"
                        variant="outline"
                    >
                        {translate.common.cancelling}
                    </Button>
                    <Button
                        className="automation-saveSend"
                        onClick={saveMessage}
                        type="button"
                    >
                        {translate.request.saveSend}
                    </Button>
                </ButtonRow>
            </>
        );
    } else {
        return (
            <ButtonRow justify="right">
                <Button
                    className="automation-addAddendum"
                    onClick={() => setIsEditing(true)}
                    type="button"
                    variant="outline"
                >
                    {translate.common.addAddendum}
                </Button>
            </ButtonRow>
        );
    }

    async function saveMessage() {
        const updatedErrors = updateValidationErrorsForOne(
            addendumValidationSchema,
            { properties: { message: message } },
            errors,
            'properties.message'
        );

        if (updatedErrors.size === 0) {
            await createAddendum({
                id:
                    props.processEntity === ProcessEntity.request
                        ? props.request.id
                        : nonNullable(props.diagnosticReport).id,
                processEntity: props.processEntity,
                properties: { message: nonNullable(message) },
                serviceCode: props.request.service.code,
                signal: abortSignal,
            });

            setIsEditing(false);
            setMessage(null);
            props.reloadProcess?.(ReloadEpisode.reloadSelf);
            props.reloadRequest?.();
        } else {
            setErrors(updatedErrors);
        }
    }

    function validate() {
        validateOne(
            addendumValidationSchema,
            { properties: { message: message } },
            'properties.message',
            errors,
            setErrors
        );
    }
}
